// src/features/health/pages/HealthReportsPage.tsx
// BP-03-04 Part 3: Health reports page

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Plus, Download, History } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { PageHeader } from '@/components/layout/PageHeader';
import { HealthReportGenerator } from '../components/reports/HealthReportGenerator';
import { HealthReportFilters } from '../components/reports/HealthReportFilters';
import { ExportHealthData } from '../components/reports/ExportHealthData';

type TabValue = 'generate' | 'history' | 'export';

export const HealthReportsPage: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const [activeTab, setActiveTab] = useState<TabValue>('generate');

  if (!childId) {
    return <div>Child ID required</div>;
  }

  const handleReportGenerated = (reportId: string) => {
    // Optionally switch to history tab to show the new report
    // setActiveTab('history');
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      <PageHeader
        title="Health Reports"
        description="Generate comprehensive reports and export health data"
        icon={<FileText className="w-6 h-6 text-sage" />}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Child', href: `/children/${childId}` },
          { label: 'Health Reports' }
        ]}
      />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)} className="mt-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="generate" className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Generate</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">History</span>
          </TabsTrigger>
          <TabsTrigger value="export" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export Data</span>
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="generate" className="mt-0">
            <HealthReportGenerator 
              childId={childId}
              onReportGenerated={handleReportGenerated}
            />
          </TabsContent>

          <TabsContent value="history" className="mt-0">
            <div className="space-y-4">
              <div className="bg-white rounded-xl border p-6">
                <h2 className="text-lg font-display font-semibold mb-4">Report History</h2>
                <HealthReportFilters childId={childId} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="export" className="mt-0">
            <ExportHealthData childId={childId} />
          </TabsContent>
        </div>
      </Tabs>

      {/* Quick Actions */}
      <div className="mt-8 p-6 bg-cream rounded-xl">
        <h3 className="font-display font-semibold mb-4">Quick Reports</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickReportButton
            title="Provider Summary"
            description="30-day overview for medical appointments"
            onClick={() => {
              setActiveTab('generate');
              // Could pre-fill form here
            }}
          />
          <QuickReportButton
            title="Medication Report"
            description="Adherence and dose history"
            onClick={() => {
              setActiveTab('generate');
            }}
          />
          <QuickReportButton
            title="Progress Report"
            description="Goals and milestones tracking"
            onClick={() => {
              setActiveTab('generate');
            }}
          />
          <QuickReportButton
            title="Tax Report"
            description="Annual medical expense summary"
            onClick={() => {
              setActiveTab('export');
            }}
          />
        </div>
      </div>
    </div>
  );
};

interface QuickReportButtonProps {
  title: string;
  description: string;
  onClick: () => void;
}

const QuickReportButton: React.FC<QuickReportButtonProps> = ({
  title,
  description,
  onClick
}) => (
  <button
    onClick={onClick}
    className="text-left p-4 bg-white rounded-lg border hover:border-sage hover:shadow-soft transition-all"
  >
    <p className="font-medium text-navy">{title}</p>
    <p className="text-sm text-warm-gray mt-1">{description}</p>
  </button>
);

export default HealthReportsPage;
